import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sun, Check, ArrowUpRight, ShieldCheck, Cpu, Layers, BadgeIndianRupee } from "lucide-react";
import Reveal from "@/components/Reveal";

const packages = [
  {
    size: "3 kW", tag: "Subsidized Home", ideal: "2–3 BHK homes", price: "₹1,45,000*",
    subsidy: "₹78,000 PM Surya Ghar", monthly: "≈ 360 units / month", popular: false,
    features: ["7 × 545 Wp TOPCon panels", "3 kW string inverter", "Elevated GI structure", "Net meter + subsidy filing"],
  },
  {
    size: "5 kW", tag: "Popular Choice", ideal: "3–4 BHK / duplex", price: "₹2,35,000*",
    subsidy: "₹78,000 PM Surya Ghar", monthly: "≈ 600 units / month", popular: true,
    features: ["9 × 545 Wp TOPCon panels", "5 kW string inverter", "Elevated GI structure", "Net meter + subsidy filing", "App monitoring included"],
  },
  {
    size: "10 kW", tag: "High Capacity", ideal: "Villas & large homes", price: "₹4,50,000*",
    subsidy: "Custom govt rebate", monthly: "≈ 1,200 units / month", popular: false,
    features: ["18 × 545 Wp TOPCon panels", "10 kW three-phase inverter", "Heavy-duty structure", "Net meter + subsidy filing", "App monitoring included"],
  },
  {
    size: "C&I", tag: "Industrial Grade", ideal: "Factories & warehouses", price: "Custom Quote",
    subsidy: "Accelerated depreciation", monthly: "Sized to your load", popular: false,
    features: ["Custom engineering design", "SCADA remote monitoring", "Depreciation advisory", "Dedicated AMC plan"],
  },
];

const components = [
  { icon: Sun, title: "Tier-1 N-Type TOPCon", text: "Higher efficiency & lower degradation in Indian heat." },
  { icon: Cpu, title: "Premium Inverters", text: "5–10 yr warranty, Wi-Fi monitoring built in." },
  { icon: Layers, title: "Leak-Proof Structures", text: "Hot-dip galvanised, wind-rated for monsoons." },
  { icon: ShieldCheck, title: "25-Yr Performance", text: "Panel performance warranty, backed by our AMC." },
];

export default function Offerings() {
  return (
    <div data-testid="offerings-page">
      <section className="relative bg-brand-deep text-white grain overflow-hidden pt-32 pb-20">
        <div className="absolute -bottom-40 -left-32 w-[28rem] h-[28rem] rounded-full bg-gold/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-mono text-xs tracking-[0.35em] text-gold mb-4"
          >
            SOLAR OFFERINGS
          </motion.p>
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "100%" }} animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] max-w-3xl"
            >
              Honest packages. <span className="text-gold">Serious hardware.</span>
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
            className="mt-6 max-w-2xl text-white/70 text-base md:text-lg leading-relaxed"
          >
            Every package includes survey, design, installation, net metering and subsidy
            filing. No hidden line items — the quote we give is the price you pay.
          </motion.p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
          {packages.map((p, i) => (
            <Reveal key={p.size} delay={i * 0.08} className="h-full">
              <div
                data-testid={`package-card-${p.size.replace(" ", "-").toLowerCase()}`}
                className={`relative h-full flex flex-col rounded-3xl p-7 border transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_60px_rgba(3,59,43,0.16)] ${
                  p.popular ? "bg-brand text-white border-brand shadow-[0_20px_50px_rgba(6,95,70,0.3)]" : "bg-white text-ink border-brand/10"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gold text-brand-deep px-4 py-1 text-[11px] font-bold tracking-widest">
                    MOST CHOSEN
                  </span>
                )}
                <div className="flex items-center justify-between">
                  <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${p.popular ? "bg-white/15 text-gold" : "bg-gold-soft text-brand"}`}>
                    {p.tag}
                  </span>
                  <Sun size={20} className={p.popular ? "text-gold" : "text-brand"} />
                </div>
                <p className="mt-6 font-display font-extrabold text-5xl">{p.size}</p>
                <p className={`text-sm mt-1 ${p.popular ? "text-white/70" : "text-moss"}`}>{p.ideal}</p>
                <p className={`mt-4 font-mono text-xs tracking-widest ${p.popular ? "text-gold" : "text-brand"}`}>{p.monthly}</p>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <Check size={15} className={`mt-0.5 shrink-0 ${p.popular ? "text-gold" : "text-brand"}`} strokeWidth={3} />
                      <span className={p.popular ? "text-white/85" : "text-ink/80"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className={`mt-7 pt-6 border-t ${p.popular ? "border-white/15" : "border-brand/10"}`}>
                  <p className="font-display font-bold text-2xl">{p.price}</p>
                  <p className={`mt-1 flex items-center gap-1.5 text-xs font-bold ${p.popular ? "text-gold" : "text-brand"}`}>
                    <BadgeIndianRupee size={13} /> {p.subsidy}
                  </p>
                  <Link
                    to="/contact"
                    data-testid={`package-cta-${p.size.replace(" ", "-").toLowerCase()}`}
                    className={`mt-5 flex items-center justify-center gap-2 rounded-full py-3 text-sm font-bold transition-transform hover:scale-[1.03] ${
                      p.popular ? "bg-gold text-brand-deep" : "bg-brand text-white"
                    }`}
                  >
                    Get This Quote <ArrowUpRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 text-xs text-moss">*Indicative pricing before subsidy. Final quote after free rooftop survey. Prices vary with roof type, structure height and cable runs.</p>
      </section>

      <section data-testid="components-strip" className="bg-brand-mist/60 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.35em] text-brand mb-4">INSIDE EVERY SYSTEM</p>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-ink">
              Components we <span className="text-brand">stake our name on.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {components.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div data-testid={`component-card-${i}`} className="rounded-3xl bg-white border border-brand/10 p-7 h-full hover:border-gold transition-colors">
                  <div className="w-12 h-12 rounded-2xl bg-gold-soft flex items-center justify-center">
                    <c.icon size={22} className="text-brand" />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-lg text-ink">{c.title}</h3>
                  <p className="mt-2 text-sm text-moss leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="mt-12 text-center">
            <Link
              to="/calculator"
              data-testid="offerings-cta-calculator"
              className="inline-flex items-center gap-2 rounded-full bg-brand text-white px-8 py-4 font-bold hover:scale-105 transition-transform"
            >
              Find My Ideal Size <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
